import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Table,
    Tag,
    Typography,
    Space,
    Button,
    Input,
    DatePicker,
    Drawer,
    Descriptions,
    Empty,
    message,
} from "antd";
import { MailOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// ---- helpers ----
function toMillis(v) {
    if (!v) return 0;
    // Firestore v7 Timestamp has toMillis()
    if (v && typeof v.toMillis === "function") return v.toMillis();
    if (typeof v === "object" && v.seconds) return v.seconds * 1000;
    return dayjs(v).valueOf();
}

function actionColor(actionType) {
    switch ((actionType || "")) {
        case "cancelRequest":
        case "returnRequest":
            return "volcano";
        case "cancelConfirm":
        case "returnConfirm":
            return "blue";
        case "updated":
            return "gold";
        case "cancelReject":
        case "returnReject":
            return "red";
        default:
            return undefined; // AntD default
    }
}

/**
 * Props:
 *  - db: firebase.firestore() instance (v7)
 *  - userOrg: { id: string } | null
 *  - filterByOrg: boolean (if true, where('organizationId', '==', userOrg.id))
 *  - filterByAction: string | undefined (e.g. "cancelRequest")
 *  - pageSize: number (default 20)
 */
export default function EmailLogViewer({
    db,
    cProps,
    user,
    userOrg,
    filterByOrg = false,
    filterByAction,
    pageSize = 20,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState("");
    const [dateRange, setDateRange] = useState([]); // [dayjs, dayjs]
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(null);

    const lastDocRef = useRef(null);
    const unsubRef = useRef(null);

    // stable key for dateRange in deps
    const rangeKey = useMemo(() => {
        if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
            return `${dateRange[0].valueOf()}|${dateRange[1].valueOf()}`;
        }
        return "";
    }, [dateRange]);

    // Build a Firestore (v7) query with server-side filters
    const buildQuery = (afterSnap = null) => {
        let q = db.collection("email_log");

        if (filterByOrg && userOrg && userOrg.id) {
            q = q.where("organizationId", "==", userOrg.id);
        }
        if (filterByAction) {
            q = q.where("actionType", "==", filterByAction);
        }

        const hasRange =
            Array.isArray(dateRange) &&
            dateRange.length === 2 &&
            dateRange[0] &&
            dateRange[1];

        if (hasRange) {
            const startDate = dateRange[0].startOf("day").toDate(); // JS Date
            const endDate = dateRange[1].endOf("day").toDate();
            console.log('hasRange', startDate, endDate)
            q = q.where("sendDate", ">=", startDate).where("sendDate", "<=", endDate);
        }

        // Must orderBy the field we range on (sendDate)
        q = q.orderBy("sendDate", "desc").limit(pageSize);

        if (afterSnap) {
            q = q.startAfter(afterSnap);
        }
        return q;
    };

    // Subscribe to initial page; re-run when filters change
    useEffect(() => {
        // reset paging when filters change
        setRows([]);
        lastDocRef.current = null;
        setLoading(true);

        const qBase = buildQuery();
        const unsub = qBase.onSnapshot(
            (snap) => {
                const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                console.log('items', items)
                setRows(items);
                lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
                setLoading(false);
            },
            (err) => {
                console.error(err);
                setLoading(false);
                message.error("Failed to load email logs");
            }
        );
        unsubRef.current = unsub;

        return () => {
            if (unsubRef.current) unsubRef.current();
        };

    }, [db, userOrg && userOrg.id, filterByOrg, filterByAction, pageSize, rangeKey]);

    // Keep paging consistent with the same server filters
    const loadMore = async () => {
        if (!lastDocRef.current) return;
        setLoadingMore(true);
        try {
            const qMore = buildQuery(lastDocRef.current);
            const snap = await qMore.get();
            if (!snap.empty) {
                const more = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setRows((prev) => [...prev, ...more]);
                lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
            } else {
                lastDocRef.current = null;
            }
        } catch (e) {
            console.error(e);
            message.error("Load more failed");
        } finally {
            setLoadingMore(false);
        }
    };

    // Client-side free-text search (Firestore doesn't support substring search)
    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let data = rows;
        if (term) {
            data = data.filter((r) => {
                const hay = [r.subject, r.email, r.userName, r.actionType]
                    .filter(Boolean)
                    .join(" · ")
                    .toLowerCase();
                return hay.includes(term);
            });
        }
        return data;
    }, [rows, search]);

    const columns = [
        {
            title: "Action",
            dataIndex: "actionType",
            key: "actionType",
            width: 120,
            render: (v) => <Tag color={actionColor(v)}>{v || "-"}</Tag>,
        },
        {
            title: "Send Date",
            dataIndex: "sendDate",
            key: "sendDate",
            width: 200,
            sorter: (a, b) => toMillis(a.sendDate) - toMillis(b.sendDate),
            defaultSortOrder: "descend",
            render: (v) => (v ? v.toDate().getFullYear() : "-"),
        },
        { title: "Subject", dataIndex: "subject", key: "subject", ellipsis: true },
        { title: "Email", dataIndex: "email", key: "email", width: 220, ellipsis: true },
        {
            title: "Organization ID",
            key: "organizationId",
            dataIndex: "organizationId",
            width: 160,
            render: (v) => (
                <Space direction="vertical" size={0}>
                    {/*<Text type="secondary">Org</Text>*/}
                    <Text code>{v || "-"}</Text>
                </Space>
            ),
        },
        {
            title: "User",
            key: "user",
            width: 200,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text>{r.userName || "-"}</Text>
                    <Text type="secondary">{r.userId || "-"}</Text>
                </Space>
            ),
        },
        {
            title: "",
            key: "actions",
            fixed: "right",
            width: 130,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        setCurrent(record);
                        setOpen(true);
                    }}
                >
                    Show
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                    <Title level={3} style={{ marginBottom: 0 }}>
                        <MailOutlined /> Email Logs
                    </Title>
                    <Space>
                        {/*<Input.Search*/}
                        {/*    allowClear*/}
                        {/*    placeholder="Search subject / email / user"*/}
                        {/*    onSearch={(v) => setSearch(v)}*/}
                        {/*    onChange={(e) => setSearch(e.target.value)}*/}
                        {/*    style={{ width: 320 }}*/}
                        {/*/>*/}
                        <RangePicker
                            allowEmpty={[true, true]}
                            onChange={(v) => setDateRange(v || [])}
                        />
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearch("");
                                setDateRange([]);
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Space>
                </Space>

                <Table
                    rowKey={(r) => r.id}
                    loading={loading}
                    dataSource={filtered}
                    columns={columns}
                    sticky
                    pagination={false}
                    locale={{ emptyText: <Empty description="No email logs" /> }}
                    scroll={{ x: 1100 }}
                />

                <div style={{ display: "flex", justifyContent: "center" }}>
                    <Button onClick={loadMore} loading={loadingMore} disabled={!lastDocRef.current}>
                        {lastDocRef.current ? "Load more" : "No more"}
                    </Button>
                </div>
            </Space>

            <Drawer
                title={
                    <Space direction="vertical" size={0}>
                        <Text type="secondary">Subject</Text>
                        <Text strong>{current?.subject || "(no subject)"}</Text>
                    </Space>
                }
                width={720}
                open={open}
                onClose={() => setOpen(false)}
                destroyOnClose
            >
                {current ? (
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <Descriptions bordered column={1} size="middle">
                            <Descriptions.Item label="Action">
                                <Tag color={actionColor(current.actionType)}>{current.actionType}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Send Date">
                                {current.sendDate ? dayjs(toMillis(current.sendDate)).format("YYYY-MM-DD HH:mm:ss") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">{current.email || "-"}</Descriptions.Item>
                            <Descriptions.Item label="User Name">{current.userName || "-"}</Descriptions.Item>
                            <Descriptions.Item label="User ID">
                                <Text code>{current.userId || "-"}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Organization ID">
                                <Text code>{current.organizationId || "-"}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Collection">{current.collection || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Doc ID">
                                <Text code>{current.docId || "-"}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                        <div>
                            <Text type="secondary">Email preview</Text>
                            <div style={{ border: "1px solid #f0f0f0", borderRadius: 12, padding: 16, background: "#fff" }}>
                                <Paragraph>
                                    <div dangerouslySetInnerHTML={{ __html: current.text || "" }} />
                                </Paragraph>
                            </div>
                        </div>
                    </Space>
                ) : (
                    <Empty />
                )}
            </Drawer>
        </div>
    );
}
